import { Center, Loader } from '@mantine/core'
import type React from 'react'
import { createContext, useContext, useEffect } from 'react'
import { authClient } from '~/api/auth-client'

// Remembers the user has logged in at least once, so the auth gate can allow
// offline access (when the session endpoint is unreachable) instead of bouncing
// to /sign-in. See routes/_app/route.tsx.
export const WAS_AUTHED_KEY = 'hs-was-authed'

export type SessionState = ReturnType<typeof authClient.useSession>

interface AuthState {
  isAuthenticated: boolean
  isPending: boolean
  session: SessionState
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const session = authClient.useSession()
  const isAuthenticated = Boolean(session.data?.user)

  useEffect(() => {
    if (isAuthenticated) localStorage.setItem(WAS_AUTHED_KEY, '1')
  }, [isAuthenticated])

  // Don't block on the session fetch while offline — it can't resolve, and the
  // auth gate allows previously-authenticated users through to offline content.
  const blocking = session.isPending && navigator.onLine

  return (
    <AuthContext.Provider value={{ session, isAuthenticated, isPending: session.isPending }}>
      {blocking ? (
        <Center h="100vh">
          <Loader size="lg" />
        </Center>
      ) : (
        children
      )}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
